// A production's features (docs/plan/Werkpakket F11): each step of the process is a feature in the menu, its tools
// are tabs on top. The nine departments stay the sign-off; this only says where each address lives. Pure.
import type { Department } from "./departments.ts";
import type { Loc, Route } from "./route.ts";
import type { ElementSubtype } from "./types.ts";

export type FeatureId = "home" | "write" | "breakdown" | "visualize" | "create" | "settings";

export const FEATURES: readonly { id: FeatureId; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "write", label: "Write" },
  { id: "breakdown", label: "Breakdown" },
  { id: "visualize", label: "Visualize" },
  { id: "create", label: "Create" },
  { id: "settings", label: "Settings" },
];

export interface FeatureTab {
  id: string;
  label: string;
  loc: Loc;
}

/** The feature a department's work lives in. The Breakdown department's address is the shot list, in Visualize. */
export const DEPT_FEATURE: Record<Department, FeatureId> = {
  writing: "write",
  breakdown: "visualize",
  casting: "breakdown",
  voice: "breakdown",
  wardrobe: "breakdown",
  sets: "breakdown",
  props: "breakdown",
  animatic: "visualize",
  music: "visualize",
  draft: "create",
  sound: "create",
  edit: "create",
  final: "create",
};

/** The group a department's square stands under on Home: the Breakdown department belongs to Breakdown there. */
export const deptGroup = (dept: Department): FeatureId =>
  dept === "breakdown" ? "breakdown" : DEPT_FEATURE[dept];

const DEPT_TAB: Record<Department, string> = {
  writing: "docs",
  breakdown: "shotlist",
  casting: "cast",
  voice: "voices",
  wardrobe: "costumes",
  sets: "locations",
  props: "props",
  animatic: "animatic",
  music: "music",
  draft: "generate",
  sound: "sound",
  edit: "edit",
  final: "final",
};

const SUBTYPE_TAB: Record<ElementSubtype, [FeatureId, string]> = {
  character: ["breakdown", "cast"],
  location: ["breakdown", "locations"],
  prop: ["breakdown", "props"],
  style: ["visualize", "mood"],
};

/** Where a route lives: its feature and tab. `subtype` is the element in view, when the route is one. */
export function placeOf(
  route: Extract<Route, { view: "project" | "shot" | "element" | "canvas" }>,
  subtype?: ElementSubtype,
): { feature: FeatureId; tab: string } {
  if (route.view === "shot") return { feature: "create", tab: "generate" };
  if (route.view === "canvas") return { feature: "visualize", tab: "mood" };
  if (route.view === "element") {
    const [feature, tab] = SUBTYPE_TAB[subtype ?? "character"];
    return { feature, tab };
  }
  const loc = route.loc;
  switch (loc.kind) {
    case "root":
    case "episodes":
      return { feature: "home", tab: "status" };
    case "assets":
    case "folder":
      return { feature: "home", tab: "files" };
    case "episode": {
      if (!loc.dept) return { feature: "home", tab: "status" };
      if (loc.dept === "writing") return { feature: "write", tab: loc.file === "script" ? "script" : "docs" };
      return { feature: DEPT_FEATURE[loc.dept], tab: DEPT_TAB[loc.dept] };
    }
    case "scene":
      return { feature: "create", tab: "generate" };
    case "cut":
      return { feature: "create", tab: "edit" };
    case "storyboard":
      return { feature: "visualize", tab: "storyboard" };
    case "tools":
      return { feature: "create", tab: "tools" };
    case "review":
      return { feature: "create", tab: "edit" };
    case "blocking":
      return { feature: "visualize", tab: "blocking" };
    case "tags":
      return { feature: "breakdown", tab: "script" };
    case "elements": {
      const [feature, tab] = SUBTYPE_TAB[loc.subtype ?? "character"];
      return { feature, tab };
    }
    case "canvases":
      return { feature: "visualize", tab: "mood" };
    case "makers":
      return { feature: "settings", tab: "makers" };
    case "crew":
      return { feature: "settings", tab: "crew" };
    case "settings":
      return { feature: "settings", tab: "general" };
  }
}

/** The tabs of a feature. Without an episode only the tabs that need none are there. */
export function featureTabs(
  feature: FeatureId,
  episodeId: string | null,
  opts: { extras?: readonly Department[]; makers?: boolean } = {},
): FeatureTab[] {
  const extra = (dept: Department) => opts.extras?.includes(dept) ?? false;
  const step = (id: string, label: string, dept: Department): FeatureTab[] =>
    episodeId ? [{ id, label, loc: { kind: "episode", id: episodeId, dept } }] : [];
  switch (feature) {
    case "home":
      return [
        { id: "status", label: "Status", loc: { kind: "root" } },
        { id: "files", label: "Files", loc: { kind: "assets" } },
      ];
    case "write":
      return episodeId
        ? [
            {
              id: "script",
              label: "Script",
              loc: { kind: "episode", id: episodeId, dept: "writing", file: "script" },
            },
            { id: "docs", label: "Docs", loc: { kind: "episode", id: episodeId, dept: "writing" } },
          ]
        : [];
    case "breakdown":
      return [
        ...(episodeId ? [{ id: "script", label: "Script", loc: { kind: "tags", episodeId } as Loc }] : []),
        ...step("cast", "Cast", "casting"),
        ...(extra("voice") ? step("voices", "Voices", "voice") : []),
        ...step("locations", "Locations", "sets"),
        ...step("props", "Props", "props"),
        ...step("costumes", "Costumes", "wardrobe"),
      ];
    case "visualize":
      return [
        { id: "mood", label: "Mood", loc: { kind: "canvases" } },
        ...step("shotlist", "Shot list", "breakdown"),
        ...(episodeId
          ? [{ id: "storyboard", label: "Storyboard", loc: { kind: "storyboard", episodeId } as Loc }]
          : []),
        ...step("animatic", "Animatic", "animatic"),
        ...(extra("music") ? step("music", "Music", "music") : []),
        ...(episodeId ? [{ id: "blocking", label: "3D", loc: { kind: "blocking", episodeId } as Loc }] : []),
      ];
    case "create":
      return [
        ...step("generate", "Generate", "draft"),
        ...(episodeId ? [{ id: "tools", label: "Tools", loc: { kind: "tools", episodeId } as Loc }] : []),
        ...(extra("sound") ? step("sound", "Sound", "sound") : []),
        ...(episodeId ? [{ id: "edit", label: "Edit", loc: { kind: "review", episodeId } as Loc }] : []),
        ...step("final", "Final", "final"),
      ];
    case "settings":
      return [
        { id: "general", label: "General", loc: { kind: "settings" } },
        { id: "crew", label: "Crew and sign-off", loc: { kind: "crew" } },
        ...(opts.makers ? [{ id: "makers", label: "Makers", loc: { kind: "makers" } as Loc }] : []),
      ];
  }
}

/** Where a feature in the menu goes: its main tab. Without an episode a feature that needs one opens Home. */
export function featureLoc(feature: FeatureId, episodeId: string | null): Loc {
  const main: Partial<Record<FeatureId, string>> = { visualize: "shotlist" };
  const tabs = featureTabs(feature, episodeId);
  const tab = tabs.find((t) => t.id === main[feature]) ?? tabs[0];
  return tab?.loc ?? { kind: "root" };
}

/** Keep the current tool while changing episode; Cut shares Edit's tab but remains the trim tool. */
export function featureEpisodeLoc(
  route: Extract<Route, { view: "project" | "shot" | "element" | "canvas" }>,
  episodeId: string,
  subtype?: ElementSubtype,
): Loc {
  if (route.view === "project" && route.loc.kind === "cut") return { ...route.loc, episodeId };
  const place = placeOf(route, subtype);
  return (
    featureTabs(place.feature, episodeId, { makers: true }).find((tab) => tab.id === place.tab)?.loc ??
    featureLoc(place.feature, episodeId)
  );
}

/** The episode an address is about, if it says. */
export function episodeOfRoute(route: Route, shotEpisode?: string | null): string | null {
  if (route.view === "shot") return shotEpisode ?? null;
  if (route.view !== "project") return null;
  const loc = route.loc;
  if (loc.kind === "episode") return loc.id;
  if (
    loc.kind === "scene" ||
    loc.kind === "cut" ||
    loc.kind === "storyboard" ||
    loc.kind === "tools" ||
    loc.kind === "review" ||
    loc.kind === "blocking" ||
    loc.kind === "tags"
  )
    return loc.episodeId;
  return null;
}
